
import React from "react";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";

interface PasswordStrengthMeterProps {
  password: string;
  validationMessage: string | null;
  isCheckingBreaches?: boolean;
}

export function PasswordStrengthMeter({ 
  password, 
  validationMessage,
  isCheckingBreaches = false
}: PasswordStrengthMeterProps) {
  // Calculate password strength
  const getPasswordStrength = (password: string): number => {
    if (!password) return 0;
    
    // Basic criteria checks
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    // Calculate score based on criteria met
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 10) score++;
    if (hasUpperCase) score++;
    if (hasLowerCase) score++;
    if (hasNumbers) score++;
    if (hasSpecialChar) score++;
    
    return Math.min(score, 4); // Cap at 4 (max strength)
  };

  // Get strength level
  const strength = getPasswordStrength(password);
  
  // Define strength labels
  const strengthLabels = ["Weak", "Fair", "Good", "Strong", "Very strong"];
  
  // Define colors for the strength levels
  const strengthColors = [
    "bg-red-500", // Weak
    "bg-orange-500", // Fair
    "bg-yellow-500", // Good
    "bg-green-500", // Strong
    "bg-emerald-600", // Very strong
  ];
  
  // Requirements for password
  const requirements = [
    { text: "At least 8 characters", met: password.length >= 8 },
    { text: "At least 1 uppercase letter", met: /[A-Z]/.test(password) },
    { text: "At least 1 lowercase letter", met: /[a-z]/.test(password) },
    { text: "At least 1 number", met: /\d/.test(password) },
    { text: "At least 1 special character", met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
    { text: "Not found in data breaches", met: !validationMessage?.includes("data breach"), checking: isCheckingBreaches },
  ];

  return (
    <div className="mt-2 space-y-2">
      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        {password && (
          <div 
            className={`h-full transition-all duration-300 ${strengthColors[strength]}`}
            style={{ width: `${(strength + 1) * 20}%` }}
          />
        )}
      </div>
      
      {/* Strength label */}
      <div className="flex justify-between items-center text-xs">
        <span className={password ? 
          (strength === 0 ? "text-red-500" : 
           strength === 1 ? "text-orange-500" : 
           strength === 2 ? "text-yellow-600" : 
           strength === 3 ? "text-green-600" : "text-emerald-700") : "text-gray-400"}>
          {password ? strengthLabels[strength] : "No password"}
        </span>
        <span className="text-muted-foreground">
          {validationMessage ? (
            <span className="flex items-center text-red-500">
              <AlertCircle className="h-3 w-3 mr-1" />
              {validationMessage}
            </span>
          ) : null}
        </span>
      </div>
      
      {/* Requirements */}
      {password && (
        <ul className="space-y-1 text-xs text-muted-foreground pt-2">
          {requirements.map((requirement, index) => (
            <li key={index} className="flex items-center">
              {requirement.checking ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin text-blue-500" />
              ) : requirement.met ? (
                <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
              ) : (
                <AlertCircle className="h-3 w-3 mr-1 text-gray-400" />
              )}
              <span className={requirement.met ? "text-green-700" : ""}>
                {requirement.text}
                {requirement.checking ? " (checking...)" : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
